import { motion } from "framer-motion";
import { useCountdown } from "@/hooks/useCountdown";
import { useAbVariant } from "@/hooks/useAbVariant";

const HERO_IMG =
  "https://images.unsplash.com/photo-1549416878-b9ca95e26903?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwYnJpZGUlMjBwb3J0cmFpdCUyMGx1eHVyeSUyMHdlZGRpbmd8ZW58MHx8fHwxNzgwNzY4ODY3fDA&ixlib=rb-4.1.0&q=85";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { h, m, s } = useCountdown();
  const { copy, variant } = useAbVariant();
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative px-6 md:px-12 lg:px-24 pt-10 md:pt-16 pb-24 md:pb-32"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-10 items-end">
        {/* Left column, type-led */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="eyebrow text-muted-foreground mb-6 md:mb-10 hidden"
          >
            By Application · Essex, London, Kent, Suffolk &amp; beyond
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.1 }}
            data-testid="hero-headline"
            className="font-serif-display tracking-tight leading-[0.95] text-[clamp(2.5rem,9vw,7rem)] break-words"
          >
            Your
            <br />
            <span className="italic">Bespoke&nbsp;</span>
            <br />
            Wedding
            <br />
            <span className="italic">Consultation.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease, delay: 0.5 }}
            className="mt-10 md:mt-14 max-w-xl"
          >
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              A <span className="font-serif-display italic">£500</span> gift,
              free for the next 24 hours.
            </p>

            <ul
              data-testid="hero-inclusions"
              className="mt-6 flex flex-wrap gap-x-3 gap-y-2 eyebrow text-foreground/60"
            >
              <li className="px-3 py-1.5 border border-border bg-secondary/60">In-studio consultation</li>
              <li className="px-3 py-1.5 border border-border bg-secondary/60">Engagement shoot</li>
              <li className="px-3 py-1.5 border border-border bg-secondary/60">Framed print</li>
              <li className="px-3 py-1.5 border border-border bg-secondary/60">Glass of champagne</li>
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href="#apply"
                data-testid="hero-primary-cta"
                data-cta-variant={variant}
                className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F6F1E8] px-7 py-5 eyebrow hover:bg-black transition-colors"
              >
                {copy.full}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
              <span className="eyebrow text-foreground/55">
                Limited spots · By application
              </span>
            </div>

            <p
              data-testid="hero-trust-line"
              className="mt-10 eyebrow text-foreground/60"
            >
              Over 950 weddings · Essex, London, Kent, Suffolk &amp; beyond
            </p>
          </motion.div>
        </div>

        {/* Right column, image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease, delay: 0.2 }}
          className="lg:col-span-5 order-1 lg:order-2 relative mb-12 lg:mb-0"
        >
          <div className="relative aspect-[3/4] overflow-hidden bg-secondary">
            <img
              src={HERO_IMG}
              alt="Bride in editorial portrait"
              loading="eager"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
          </div>

          {/* Invitation card, flows in-document on mobile, floats only on lg+ */}
          <div className="mt-6 lg:mt-0 lg:absolute lg:-bottom-10 lg:-left-10 bg-[#F6F1E8] border border-border p-5 md:p-6 lg:max-w-[280px] shadow-[0_8px_30px_rgba(26,26,26,0.06)]">
            <p className="eyebrow text-muted-foreground mb-3">The Invitation</p>
            <p className="font-serif-display text-xl md:text-[1.6rem] leading-snug tracking-tight">
              A <span className="italic">£500</span> gift, currently free.
            </p>
            <div className="hairline my-4" />
            <p
              data-testid="hero-countdown"
              className="font-sans-ui text-sm tabular-nums text-foreground/70"
            >
              {pad(h)}h : {pad(m)}m : {pad(s)}s remaining
            </p>
          </div>
        </motion.div>
      </div>

      {/* Awards strip */}
      <div
        data-testid="hero-awards-strip"
        className="mt-20 md:mt-32 border-y border-border py-8 md:py-10"
      >
        <p className="eyebrow text-center text-muted-foreground/80 mb-6">
          Officially Recognised &amp; Awarded By
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-16 lg:gap-x-24">
          <img
            src="/awards/swpp.png"
            alt="Society of Wedding &amp; Portrait Photographers"
            loading="lazy"
            className="h-10 sm:h-12 md:h-14 w-auto opacity-80 hover:opacity-100 transition-opacity"
          />
          <img
            src="/awards/portrait-of-the-year.png"
            alt="The International Portrait Photographer of the Year"
            loading="lazy"
            className="h-8 sm:h-10 md:h-11 w-auto opacity-80 hover:opacity-100 transition-opacity"
          />
        </div>
      </div>
    </section>
  );
}
